import React, { FC } from 'react';
import { styled, Box, Grid, FormControlLabel, Radio } from '@mui/material';
import { FieldWrapper } from 'DesignSystem';
import DropDown from 'Components/Forms/DropDown';
import { TCsvSeparator, TDecimalSeparator } from 'FeatureModules/DataImport/types/dataset.types';
import { IFileConfiguration } from 'FeatureModules/DataImport/types/dataset.types';
import {
  CSV_SEPARATOR_OPTIONS,
  DECIMAL_SEPARATOR_OPTIONS,
} from 'FeatureModules/DataImport/utils/dataImport.utils';
import { Field } from 'formik';
import { RadioGroup } from 'formik-mui';
import { Tbooleanish } from 'types/booleanish.types';

interface IFileProcessingConfigProps {
  fileConfiguration: IFileConfiguration;
  displayCsvSeparator?: boolean;
  displayAutoClosingGeometriesSelection?: boolean;
  onFileConfigurationChange: (fileConfiguration: IFileConfiguration) => void;
}

const CSV_DROPDOWN_OPTIONS = Object.entries(CSV_SEPARATOR_OPTIONS).map(([key, { label }]) => ({
  key: key as TCsvSeparator,
  label,
}));

const DECIMAL_DROPDOWN_OPTIONS = Object.entries(DECIMAL_SEPARATOR_OPTIONS).map(
  ([key, { label }]) => ({
    key: key as TDecimalSeparator,
    label,
  })
);

const Container = styled(Box)(({ theme }) => ({
  background: theme.custom.themeColors.grayScale[5],
  borderRadius: 4,
  border: `1px solid ${theme.custom.themeColors.grayScale[10]}`,
  padding: theme.spacing(2),
}));

const DropdownContainer = styled('div')(({ theme }) => ({
  '& .MuiInputBase-root': {
    width: '100% !important',
    backgroundColor: '#fff !important',
  },

  '& .MuiSelect-select': {
    padding: '4px 8px !important',
    border: `1px solid ${theme.custom.themeColors.grayScale[20]}`,
    borderRadius: 4,
    backgroundColor: '#fff',
    width: '100%',
  },
}));

const FileProcessingConfig: FC<IFileProcessingConfigProps> = ({
  fileConfiguration,
  displayCsvSeparator,
  displayAutoClosingGeometriesSelection,
  onFileConfigurationChange,
}) => {
  return (
    <Container>
      <Grid container spacing={2}>
        {displayAutoClosingGeometriesSelection && (
          <Grid item xs={displayCsvSeparator ? 6 : 12}>
            <FieldWrapper
              label="Auto close open geometries in GeoJSON files"
              width="100%"
              tooltip={{
                variant: 'INFO',
                helperText:
                  'When enabled, this option will automatically close any open geometries in GeoJSON files during processing.',
              }}
            >
              <Field
                fullWidth
                size="small"
                component={RadioGroup}
                variant="outlined"
                onChange={(_: unknown, value: Tbooleanish) => {
                  onFileConfigurationChange({
                    autoCloseGeojsonGeometries: value === 'true',
                    csvSeparator: fileConfiguration.csvSeparator,
                    decimalSeparator: fileConfiguration.decimalSeparator,
                  });
                }}
                value={fileConfiguration.autoCloseGeojsonGeometries ? 'true' : 'false'}
              >
                <FormControlLabel
                  value="true"
                  control={<Radio size="small" />}
                  label="Auto close"
                />
                <FormControlLabel
                  value="false"
                  control={<Radio size="small" />}
                  label="Do not auto close"
                />
              </Field>
            </FieldWrapper>
          </Grid>
        )}
        <Grid item xs={displayCsvSeparator ? 6 : 12}>
          <FieldWrapper
            label="Default file decimal separator"
            width="100%"
            tooltip={{
              variant: 'INFO',
              helperText:
                'The character used to separate the integer part from the fractional part of a number. Common examples include ‘.’ (dot) in English or ‘,’ (comma) in many European countries. Ensure the chosen separator matches the formatting conventions of the data or the system you are working with.',
            }}
          >
            <DropdownContainer>
              <DropDown<{ key: TDecimalSeparator; label: string }>
                options={DECIMAL_DROPDOWN_OPTIONS}
                // in order to display the placeholder correctly, the value must be an empty string
                currentValue={DECIMAL_DROPDOWN_OPTIONS.find(
                  option => option.key === fileConfiguration.decimalSeparator
                )}
                onChange={option => {
                  onFileConfigurationChange({
                    decimalSeparator: option.key,
                    csvSeparator: fileConfiguration.csvSeparator,
                    autoCloseGeojsonGeometries: fileConfiguration.autoCloseGeojsonGeometries,
                  });
                }}
                renderItems={item => <div>{item.label}</div>}
                placeholder="Dot (Default)"
                fullWidth
              />
            </DropdownContainer>
          </FieldWrapper>
        </Grid>
        {displayCsvSeparator && (
          <Grid item xs={6}>
            <FieldWrapper
              label="Default csv separator"
              tooltip={{
                variant: 'INFO',
                helperText:
                  '“The character used to separate individual values in a CSV (Comma-Separated Values) file. Common separators include ‘,’ (comma) or ‘;’ (semicolon). Use the appropriate separator based on the regional settings or the software that will process the file.',
              }}
            >
              <DropdownContainer>
                <DropDown<{ key: TCsvSeparator; label: string }>
                  options={CSV_DROPDOWN_OPTIONS}
                  // in order to display the placeholder correctly, the value must be an empty string
                  currentValue={CSV_DROPDOWN_OPTIONS.find(
                    option => option.key === fileConfiguration.csvSeparator
                  )}
                  onChange={option => {
                    onFileConfigurationChange({
                      csvSeparator: option.key,
                      decimalSeparator: fileConfiguration.decimalSeparator,
                      autoCloseGeojsonGeometries: fileConfiguration.autoCloseGeojsonGeometries,
                    });
                  }}
                  renderItems={item => <div>{item.label}</div>}
                  placeholder="Detect automatically (Default)"
                  fullWidth
                />
              </DropdownContainer>
            </FieldWrapper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default FileProcessingConfig;
